import React, { createContext, useState, useEffect, useCallback, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { useSkipAutoPopups } from '../hooks/useSkipAutoPopups';
import popupService from '../services/popupService';
import type { Popup } from '../types/popup.types';
import { shouldShowPopup, markPopupAsShown } from '../utils/popupHelpers';

interface PopupContextValue {
  popups: Popup[];
  activePopup: Popup | null;
  loading: boolean;
  error: string | null;
  closePopup: () => void;
  refreshPopups: () => Promise<void>;
}

export const PopupContext = createContext<PopupContextValue | undefined>(undefined);

const REFRESH_INTERVAL =
  process.env.NODE_ENV === 'development'
    ? 30 * 1000 // 30 saniye (development test için)
    : 3 * 60 * 1000; // 3 dakika (production)

interface PopupProviderProps {
  children: React.ReactNode;
  vendorId: number;
}

export const PopupProvider: React.FC<PopupProviderProps> = ({ children, vendorId }) => {
  const location = useLocation();
  const skipAutoPopups = useSkipAutoPopups();
  const [popups, setPopups] = useState<Popup[]>([]);
  const [activePopup, setActivePopup] = useState<Popup | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [shownOnCurrentPage, setShownOnCurrentPage] = useState<Set<number>>(new Set());
  const previousPathRef = useRef<string>(location.pathname);
  const intervalRef = useRef<NodeJS.Timeout | null>(null);
  const isActiveRef = useRef(true);

  // Popup'ları fetch et
  const fetchPopups = useCallback(async () => {
    if (!vendorId) return;

    try {
      setError(null);
      const data = await popupService.getPopupsForVendor(vendorId);
      setPopups(data);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Unknown error';
      if (errorMessage !== 'Request cancelled') {
        setError(errorMessage);
      }
    } finally {
      setLoading(false);
    }
  }, [vendorId]);

  // İlk yükleme
  useEffect(() => {
    fetchPopups();
  }, [fetchPopups]);

  // Gösterilecek popup'ı belirle
  useEffect(() => {
    const currentPath = location.pathname;

    // URL değişti mi kontrol et
    if (currentPath !== previousPathRef.current) {
      previousPathRef.current = currentPath;
      setShownOnCurrentPage(new Set());
      setActivePopup(null);
    }

    if (skipAutoPopups) {
      if (activePopup) setActivePopup(null);
      return;
    }

    if (!activePopup && popups.length > 0) {
      for (const popup of popups) {
        if (shownOnCurrentPage.has(popup.id)) {
          continue;
        }

        if (shouldShowPopup(popup, currentPath, vendorId)) {
          setActivePopup(popup);
          break;
        }
      }
    }
  }, [location.pathname, popups, activePopup, vendorId, shownOnCurrentPage, skipAutoPopups]);

  // Auto-refresh mekanizması
  useEffect(() => {
    const startInterval = () => {
      intervalRef.current = setInterval(() => {
        if (isActiveRef.current && !document.hidden) {
          fetchPopups();
        }
      }, REFRESH_INTERVAL);
    };

    const handleVisibilityChange = () => {
      if (document.hidden) {
        if (intervalRef.current) {
          clearInterval(intervalRef.current);
          intervalRef.current = null;
        }
      } else {
        if (isActiveRef.current && !intervalRef.current) {
          fetchPopups();
          startInterval();
        }
      }
    };

    startInterval();
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      isActiveRef.current = false;
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [fetchPopups]);

  // Popup'ı kapat
  const closePopup = useCallback(() => {
    if (activePopup) {
      markPopupAsShown(activePopup);
      setShownOnCurrentPage((prev) => new Set(prev).add(activePopup.id));
      setActivePopup(null);
    }
  }, [activePopup]);

  const value: PopupContextValue = {
    popups,
    activePopup,
    loading,
    error,
    closePopup,
    refreshPopups: fetchPopups,
  };

  return <PopupContext.Provider value={value}>{children}</PopupContext.Provider>;
};
